import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ServicesProvider, ServicesProviderScrapingConfig } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Input type for creating an inventory scraper (without id, injected from provider)
 */
export interface CreateInventoryScraperInput {
	scrapingConfig: ServicesProviderScrapingConfig;
}

/**
 * API function to create an inventory scraper for a services provider
 */
export const createInventoryScraper = async (
	servicesProviderId: string,
	input: CreateInventoryScraperInput,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.inventoryScraper(servicesProviderId),
		input,
	);
	return response.data;
};

/**
 * Hook for creating an inventory scraper for a services provider
 */
export const useCreateInventoryScraper = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: CreateInventoryScraperInput) => {
			if (!servicesProviderId) {
				throw new Error(
					'Unable to create inventory scraper without a services provider ID.',
				);
			}
			return createInventoryScraper(servicesProviderId, input);
		},
		onSuccess: (data) => {
			// Optimistic cache update
			queryClient.setQueryData(queries.servicesProviders.detail(data.id).queryKey, data);
		},
	});
};
